const DAY_MS = 86_400_000;

/**
 * Parses the two shapes an invoice `dueDate` reaches the FE in.
 *
 * `Invoice.dueDate` is typed `string | null` in @repo/shared-types: the API
 * serialises a Prisma `DateTime` (`"2026-08-20T00:00:00.000Z"`), while values
 * that originate from a `DatePicker` inside the app (the bulk re-date dialog,
 * chốt sổ) are bare `"YYYY-MM-DD"`. Both are wall-clock-as-UTC, so a bare date
 * is read as UTC midnight, never as browser-local midnight.
 *
 * Returns null for null/undefined/empty and for anything unparseable — a bad
 * date must not throw inside a table row.
 */
export function parseDueDate(dueDate: string | null | undefined): Date | null {
  if (!dueDate) return null;
  const ms = Date.parse(dueDate.length === 10 ? `${dueDate}T00:00:00.000Z` : dueDate);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/**
 * Whole days between an invoice's due date and "now", floored, never negative
 * — the "· trễ {{n}} ngày" the overdue row carries (design §3).
 *
 * A deliberate FE mirror of `overdueDays` in
 * `apps/api/src/services/invoiceStatus.ts` (same convention
 * `bulkInvoiceSelection.ts` uses to mirror `isInvoicePayable`), rather than a
 * read of the wire's `Invoice.daysOverdue`: the server compares raw instants,
 * while this compares UTC **day indices** — the same flooring convention
 * `sessionTime.ts` uses for every wall-clock-as-UTC field — so a due date
 * stamped at 17:30 still reads "trễ 1 ngày" the following morning instead of
 * "trễ 0 ngày".
 *
 * The FE whole-day VN count may differ by one from the server's
 * `daysOverdue` used in reminder text; the FE number is the one a VN teacher
 * expects — recorded in the roadmap.
 *
 * Pass `now` from `useServerNow()` (the VN wall-clock server instant), never
 * `new Date()`.
 */
export function overdueDays(dueDate: string | null | undefined, now: Date): number {
  const due = parseDueDate(dueDate);
  if (!due) return 0;
  const dueDay = Math.floor(due.getTime() / DAY_MS);
  const today = Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / DAY_MS);
  return Math.max(0, today - dueDay);
}
